/* eslint-disable max-lines */
/* eslint-disable no-param-reassign */
/* eslint-disable function-paren-newline */
import moment from 'moment';

import {
  enrichmentStatusOptions,
  companyNameIncludedOptions,
  companySizeOptions,
  fundOptions,
  inPortcoOptions,
  jobSearchStatusesOptions,
  jobTitlesIncludedOptions,
  jobTitlesMatchOptions,
  locationRadiusOptions,
  resumeOptions,
  signUpDayOptions,
  signupInfoOptions,
  signupSenioritiesOptions,
  signupWorkEnvironmentsOptions,
  seniorityOptions,
  jobApplicationsOptions,
  contactSourceOptions,
  companyFoundedYearIncludedOptions,
  numberOfYearOptions,
} from 'components/organisms/contactsFilters/defaultFilters';
import { monthOptions } from 'components/atoms/periodDropDown/date';
import { TimeZoneConstants } from 'components/organisms/contactsFilters/components/filters/timeZone/constants';
import { trackEvent } from '../../helpers/analytics';

export const NO_VALUE_SENTINEL = '__no_value__';

/**
 * Removes empty arrays, null/undefined values, and UI-only default values from filters
 * before sending to the API. This ensures clean payloads with only meaningful filter values.
 *
 * @param {Object} filters - The filters object to clean
 * @returns {Object} Cleaned filters object with only relevant values
 */
export const removeEmptyAndDefaultFilters = (filters = {}) =>
  Object.keys(filters).reduce((acc, key) => {
    const value = filters[key];

    // Skip empty arrays
    if (Array.isArray(value) && value.length === 0) {
      return acc;
    }

    // Skip null/undefined values
    if (typeof value === 'undefined' || value === null) {
      return acc;
    }

    // Skip UI-only default values that shouldn't be sent to backend
    // These are filter display options, not actual search criteria
    if (key === 'jobTitlesMatch' && value?.value === 'contains') {
      return acc;
    }
    // Only skip jobTitlesIncluded='current' if there's no jobTitles filter
    if (key === 'jobTitlesIncluded' && value?.value === 'current' && !filters.jobTitles?.length) {
      return acc;
    }
    // Only skip companyNameIncluded='current' if there's no companyName or companyDomain filter
    if (
      key === 'companyNameIncluded' &&
      value?.value === 'current' &&
      !filters.companyName?.length &&
      !filters.companyDomain?.length
    ) {
      return acc;
    }
    // Always include foundedYearIncluded if there's a foundedYear filter (even if it's 'current')
    // Only skip it if there's no foundedYear filter at all
    if (key === 'foundedYearIncluded' && !filters.foundedYear) {
      return acc;
    }
    // locationRadius is only relevant when there's a locality-based location filter
    if (key === 'locationRadius' && !filters.locationIds?.some((item) => item.type === 'locality')) {
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});

export const parseFiltersData = (filters = {}, enableStringify = false) =>
  Object.keys(filters).reduce((carry, key) => {
    if (key === 'jobApplicationsJobs') {
      carry.jobApplicationsJobs = filters[key].map((item) => ({
        id: parseInt(item.value, 10),
        name: item.label,
      }));
      return carry;
    }

    if (key === 'sources') {
      carry[key] = filters[key].reduce((acc, item) => [...acc, ...item.value], []);

      return carry;
    }

    if (key === 'jobApplications') {
      if (filters[key].value === null) {
        // 'Any' selected - don't add filter
        return carry;
      }

      if (parseInt(filters[key].value, 10) === 0) {
        carry.jobApplicationsCountLteq = 0;
      } else {
        carry.jobApplicationsCountGteq = parseInt(filters[key].value, 10);
      }

      return carry;
    }
    // Always keep plain objects — never JSON.stringify. POST /contacts/search expects each
    // custom_fields element to be a hash; stringify breaks validation ("must be a hash").
    // enableStringify is for query-string cache keys; axios still JSON-serializes nested objects.
    if (key === 'customFields' && Array.isArray(filters[key])) {
      carry.custom_fields = (carry.custom_fields || []).concat(filters[key]);
      return carry;
    }
    if (key.startsWith('customFields_')) {
      const rawId = key.replace('customFields_', '');
      const fieldId = /^\d+$/.test(rawId) ? parseInt(rawId, 10) : rawId;
      if (!carry.custom_fields) {
        carry.custom_fields = [];
      }
      const values = filters[key];
      if (Array.isArray(values) && values.length > 0) {
        values.forEach((item) => {
          const raw = typeof item === 'object' && item !== null ? item.value : item;
          if (raw === NO_VALUE_SENTINEL) {
            carry.custom_fields.push({ id: fieldId });
          } else {
            carry.custom_fields.push({ id: fieldId, value: raw });
          }
        });
      }
      return carry;
    }

    if (key === 'sortBy' && filters[key]) {
      carry.sortBy = filters[key];
      return carry;
    }

    if (key === 'sortDirection' && filters[key]) {
      carry.sortDirection = filters[key];
      return carry;
    }

    if (['companyStagesYears', 'industryTagsYears', 'bizModelYears', 'locationRadius'].includes(key)) return carry;

    if (key === 'activePositionsOrganizationStages' && filters[key]?.length) {
      carry[key] = filters[key].map((item) => item.from?.value);
      return carry;
    }

    if (key === 'stageTransitions' && filters[key]?.length) {
      carry[key] = filters[key].map((item) => {
        const value = {
          stage_from: item.from.value,
          stage_to: item.to.value,
        };
        return enableStringify ? JSON.stringify(value) : value;
      });

      return carry;
    }

    if (key === 'companyDomain' && filters[key]?.length) {
      // Split by newlines, commas, and spaces, then clean up each domain
      carry[key] = filters[key]
        .map(
          (item) =>
            item
              .split(/[\n, ]+/)
              .map((domain) => {
                // Remove whitespace
                let cleaned = domain.trim();
                // Remove protocol (http://, https://, etc.)
                cleaned = cleaned.replace(/^https?:\/\//, '');
                // Remove www.
                cleaned = cleaned.replace(/^www\./, '');
                // Remove trailing slashes
                cleaned = cleaned.replace(/\/+$/, '');
                return cleaned;
              })
              .filter((domain) => domain.length > 0), // Remove empty strings
        )
        .flat();
    } else if (key === 'locationIds' && filters[key]?.length) {
      carry[key] = filters[key].map((item) => {
        const value = {
          place_id: item.value,
          type: item.type,
          title: item.label,
        };

        return enableStringify ? JSON.stringify(value) : value;
      });

      if (filters[key].some((item) => item.type === 'locality')) {
        carry.locationRadius = filters.locationRadius?.value ?? 10;
      }
    } else if (key === 'companyStages' && filters[key]?.length) {
      carry[key] = filters[key].map((item) => {
        const value = {
          years: item.from?.value ?? 2,
          stage: item.to.value,
        };
        return enableStringify ? JSON.stringify(value) : value;
      });
    } else if (key === 'industryTags' && filters.industryTags?.length) {
      carry[key] = filters[key].map((item) => {
        const value = {
          industry_tag: item?.value,
          years: filters.industryTagsYears?.value ?? 2,
        };

        return enableStringify ? JSON.stringify(value) : value;
      });
    } else if (key === 'bizModels' && filters.bizModels?.length) {
      carry[key] = filters[key].map((item) => item.value);
      carry.biz_model_years = filters.bizModelYears?.value ?? 2;
      return carry;
    } else if (key === 'companyNamePeriod' && Object.keys(filters[key] ?? {}).length > 0) {
      const { fromMonth, fromYear, toMonth, toYear } = filters[key];

      carry[`${key}From`] = `${fromYear}-${fromMonth.value + 1}-01`;
      carry[`${key}To`] = `${toYear}-${toMonth.value + 1}-${moment(`${toYear}-${toMonth.value + 1}-01`)
        .endOf('month')
        .format('DD')}`;
    } else if (Array.isArray(filters[key]) && typeof filters[key] !== 'string') {
      carry[key] = filters[key].map((item) => (typeof item === 'string' ? item : item.value));
    } else if (filters[key]?.value || typeof filters[key]?.value === 'boolean') {
      carry[key] = filters[key].value;
    } else {
      carry = { ...carry, ...filters[key] };
    }

    return carry;
  }, {});

export const parseFilterValueForTrackingEvents = (key, value) => {
  if (key === 'yearsOfExperience' && !!value) {
    return [value.years_experience_gteq, value.years_experience_lteq];
  }

  if (key === 'yearsInCurrentCompany' && !!value) {
    return [value.years_in_current_company_gteq, value.years_in_current_company_lteq];
  }

  if (key === 'foundedYear' && !!value) {
    return [value.founded_year_gteq, value.founded_year_lteq];
  }

  if (key === 'sources') {
    return value.reduce((acc, item) => [...acc, ...item.value], []);
  }

  if ((typeof value === 'function' || typeof value === 'object') && !!value && !Array.isArray(value)) {
    return value.value;
  }

  if (Array.isArray(value) && value.length) {
    if (Object.prototype.hasOwnProperty.call(value[0], 'place_id')) {
      return value.map((item) => item.place_id);
    }
    if (Object.prototype.hasOwnProperty.call(value[0], 'value')) {
      return value.map((item) => item.value);
    }
  }

  return value;
};

export const filtersCount = (filters) =>
  Object.keys(filters).reduce(
    (carry, idx) => (filters[idx].length || Object.keys(filters[idx]).length ? carry + 1 : carry),
    0,
  );

const mapArrayToObj = (arr) => arr.reduce((carry, item) => ({ ...carry, [item.value]: item }), {});

const mappedOptions = {
  enrichmentStatus: mapArrayToObj(enrichmentStatusOptions),
  jobTitlesIncluded: mapArrayToObj(jobTitlesIncludedOptions),
  jobTitlesMatch: mapArrayToObj(jobTitlesMatchOptions),
  locationRadius: mapArrayToObj(locationRadiusOptions),
  currentCompanySizes: mapArrayToObj(companySizeOptions),
  fundOptions: mapArrayToObj(fundOptions),
  signupHasResume: mapArrayToObj(resumeOptions),
  signupDaysSinceJoined: mapArrayToObj(signUpDayOptions),
  signupJobSearchStatuses: mapArrayToObj(jobSearchStatusesOptions),
  signupSeniorities: mapArrayToObj(signupSenioritiesOptions),
  signupWorkEnvironment: mapArrayToObj(signupWorkEnvironmentsOptions),
  companyNameIncluded: mapArrayToObj(companyNameIncludedOptions),
  hasSignupInfo: mapArrayToObj(signupInfoOptions),
  seniorities: mapArrayToObj(seniorityOptions),
  foundedYearIncluded: mapArrayToObj(companyFoundedYearIncludedOptions),
  tzAbbreviation: mapArrayToObj(TimeZoneConstants),
};

export const reverseParsedFilters = (filters = {}, omit = []) => {
  const keysToOmit = new Set(omit);
  return Object.keys(filters).reduce((carry, key) => {
    if (keysToOmit.has(key)) return carry;
    const directFilters = [
      'enrichmentStatus',
      'locationRadius',
      'jobTitlesIncluded',
      'jobTitlesMatch',
      'signupHasResume',
      'signupDaysSinceJoined',
      'companyNameIncluded',
      'hasSignupInfo',
      'foundedYearIncluded',
    ];

    if (directFilters.includes(key)) {
      carry[key] = mappedOptions[key][filters[key]];
      return carry;
    }

    if (key === 'sources') {
      carry[key] = filters[key]
        .map((item) => contactSourceOptions.find((option) => option.value.includes(item)))
        .filter(Boolean);
      return carry;
    }

    if (key === 'jobApplicationsJobs' && filters[key]?.length > 0) {
      carry[key] = filters[key].map((item) => ({
        value: item.id,
        label: item.name,
      }));
      carry.jobApplications = jobApplicationsOptions[1];
      return carry;
    }

    if (key === 'jobApplicationsCountGteq') {
      carry.jobApplications = jobApplicationsOptions[1];
      return carry;
    }

    if (key === 'jobApplicationsCountLteq') {
      carry.jobApplications = jobApplicationsOptions[2];
      return carry;
    }

    if (key === 'inPortco') {
      const inPortcoByValue = inPortcoOptions.reduce((acc, option) => {
        acc[String(option.value)] = option;
        return acc;
      }, {});
      carry[key] = inPortcoByValue[String(filters[key] ?? 'any')];
      return carry;
    }

    const preferenceFilters = ['signupSeniorities', 'signupWorkEnvironment', 'signupJobSearchStatuses'];
    if (preferenceFilters.includes(key)) {
      carry[key] = filters[key].map((item) => mappedOptions[key][item]);
      return carry;
    }

    const arrayFilters = ['seniorities', 'currentCompanySizes'];
    if (arrayFilters.includes(key) && filters[key]) {
      carry[key] = filters[key].map((item) => mappedOptions[key][item]);
      return carry;
    }

    if (key === 'keywords' && filters.keywords) {
      if (filters.keywords.length > 1) {
        carry[key] = [
          filters.keywords
            .map((keyword) => {
              if (keyword.startsWith('"') && keyword.endsWith('"')) {
                return keyword;
              }
              return keyword.includes(' ') ? `"${keyword}"` : keyword;
            })
            .join(' OR '),
        ];
      } else {
        carry[key] = filters.keywords;
      }

      return carry;
    }

    if (key === 'yearsInCurrentCompanyLteq' && filters.yearsInCurrentCompanyLteq) {
      carry.yearsInCurrentCompany = {
        years_in_current_company_lteq: filters.yearsInCurrentCompanyLteq,
        years_in_current_company_gteq: filters.yearsInCurrentCompanyGteq,
      };
      return carry;
    }

    if (key === 'yearsExperienceLteq' && filters.yearsExperienceLteq) {
      carry.yearsOfExperience = {
        years_experience_lteq: filters.yearsExperienceLteq,
        years_experience_gteq: filters.yearsExperienceGteq,
      };
      return carry;
    }

    if (key === 'foundedYearLteq' && filters.foundedYearLteq) {
      carry.foundedYear = {
        founded_year_lteq: filters.foundedYearLteq,
        founded_year_gteq: filters.foundedYearGteq,
      };
      return carry;
    }

    if (key === 'stageTransitions' && filters.stageTransitions) {
      carry[key] = filters.stageTransitions.map((item, index) => {
        const fund = {
          id: index,
          stage: 'growth',
          from: mappedOptions.fundOptions[item.stageFrom === 'series_c' ? 'series_c_plus' : item.stageFrom],
          to: mappedOptions.fundOptions[item.stageTo === 'series_c' ? 'series_c_plus' : item.stageTo],
        };

        fund.key = JSON.stringify(fund);
        return fund;
      });
      return carry;
    }

    if (key === 'importerIds' && filters.importerIds) {
      carry[key] = filters.importerIds.map((item) => ({
        value: item.id,
        label: item.fullName,
      }));
      return carry;
    }

    if (key === 'activePositionsOrganizationStages' && filters.activePositionsOrganizationStages) {
      carry[key] = filters.activePositionsOrganizationStages.map((item) => {
        const stage = {
          index: item.index,
          stage: 'current',
          from: mappedOptions.fundOptions[item === 'series_c' ? 'series_c_plus' : item],
        };

        stage.key = JSON.stringify(stage);
        return stage;
      });
      return carry;
    }

    if (key === 'companyStages' && filters.companyStages) {
      carry[key] = filters.companyStages.map((item, index) => {
        const stage = {
          id: index,
          stage: 'early',
          from: {
            label: item.years || 2,
            value: item.years || 2,
          },
          to: mappedOptions.fundOptions[item.stage === 'series_c' ? 'series_c_plus' : item.stage],
        };

        stage.key = JSON.stringify(stage);
        return stage;
      });
      return carry;
    }

    if (key === 'locationIds' && filters.locationIds) {
      carry[key] = filters.locationIds.map((item) => ({
        value: item.placeId,
        label: item.title,
        type: item.type,
        title: item.title,
      }));
      return carry;
    }

    if (key === 'industryTags' && filters?.industryTags?.length) {
      carry[key] = filters.industryTags.map(({ industryTag }) => ({
        label: industryTag,
        value: industryTag,
      }));

      const yearsValue = filters?.industryTags[0]?.years || 2;
      carry.industryTagsYears = numberOfYearOptions.find((opt) => opt.value === yearsValue) || numberOfYearOptions[0];

      return carry;
    }

    if (key === 'bizModels' && filters?.bizModels?.length) {
      carry[key] = filters.bizModels.map((bizModel) => ({
        label: typeof bizModel === 'string' ? bizModel : bizModel.bizModel,
        value: typeof bizModel === 'string' ? bizModel : bizModel.bizModel,
      }));
      const yearsValue = filters.bizModelYears || 2;
      carry.bizModelYears = numberOfYearOptions.find((opt) => opt.value === yearsValue) || numberOfYearOptions[0];

      return carry;
    }

    if (key === 'companyNamePeriodFrom' && filters.companyNamePeriodFrom) {
      const [year, month] = filters.companyNamePeriodFrom.split('-');

      carry.companyNamePeriod = {
        fromYear: year,
        fromMonth: monthOptions()[parseInt(month, 10) - 1],
        ...carry.companyNamePeriod,
      };

      return carry;
    }

    if (key === 'companyNamePeriodTo' && filters.companyNamePeriodTo) {
      const [year, month] = filters.companyNamePeriodTo.split('-');

      carry.companyNamePeriod = {
        toYear: year,
        toMonth: monthOptions()[parseInt(month, 10) - 1],
        ...carry.companyNamePeriod,
      };

      return carry;
    }

    if (key === 'companyDomain' && filters.companyDomain) {
      // companyDomain is already an array of strings, no transformation needed
      carry[key] = filters.companyDomain;
      return carry;
    }

    const value = filters[key];

    if (Array.isArray(value)) {
      carry[key] = value.map((item) => ({
        value: item,
        label: item,
      }));
    }

    return carry;
  }, {});
};

export const trackContactFiltersEvent = ({
  group,
  newFilters,
  activeNetwork,
  trackingLocation,
  sharedList,
  customFields = [],
}) => {
  const filterNames = new Set([
    'jobTitles',
    'locationIds',
    'companyName',
    'companyStages',
    'seniorities',
    'currentCompanySizes',
    'industryTags',
    'tzAbbreviation',
    'bizModels',
    'skills',
    'sharedListIds',
    'tags',
    'importerIds',
    'yearsOfExperience',
    'yearsInCurrentCompany',
    'foundedYear',
    'keywords',
    'inPortco',
    'hasSignupInfo',
    'signupJobFunctions',
    'signupSeniorities',
    'signupWorkEnvironments',
    'signupLocations',
    'signupJobSearchStatuses',
    'signupHasResume',
    'signupDaysSinceJoined',
    'enrichmentStatus',
    'sources',
  ]);

  const mapper = {
    jobTitles: new Set(['jobTitlesIncluded', 'jobTitlesMatch']),
    stageTransitions: new Set(['stageTransitions', 'activePositionsOrganizationStages']),
    industryTagsYears: new Set(['industryTagsYears']),
    bizModels: new Set(['bizModelYears']),
    companyName: new Set(['companyNameIncluded', 'companyNamePeriod']),
    foundedYear: new Set(['foundedYearIncluded']),
    locationIds: new Set(['locationIds']),
  };
  const reverseMapper = new Map([
    ['jobTitlesIncluded', 'jobTitles'],
    ['jobTitlesMatch', 'jobTitles'],
    ['stageTransitions', 'stageTransitions'],
    ['activePositionsOrganizationStages', 'stageTransitions'],
    ['industryTagsYears', 'industryTagsYears'],
    ['bizModelYears', 'bizModels'],
    ['companyNameIncluded', 'companyName'],
    ['companyNamePeriod', 'companyName'],
    ['foundedYearIncluded', 'foundedYear'],
  ]);

  const eventPayload = {};

  Object.keys(group).forEach((key) => {
    if (key.startsWith('customFields_')) {
      const fieldId = Number(key.replace('customFields_', ''));
      const field = customFields.find((f) => f.id === fieldId);
      eventPayload.filterName = field?.name || key;
      eventPayload.customFieldId = fieldId;
      const values = newFilters[key];
      if (Array.isArray(values) && values.length > 0) {
        eventPayload.selectedValues = values.map((item) =>
          typeof item === 'object' && item !== null ? item.label || item.value : item,
        );
      } else {
        eventPayload.selectedValues = [];
      }
      return;
    }

    const filterName = filterNames.has(key) ? key : reverseMapper.get(key);
    eventPayload.filterName = filterName;
    eventPayload.selectedValues = parseFilterValueForTrackingEvents(filterName, newFilters[filterName]);
    const options = Object.keys(newFilters).filter((filterKey) => mapper[key]?.has?.(filterKey));
    if (options.length) {
      eventPayload.options = options;
    }
  });

  trackEvent('contacts:filter_change', {
    collection_id: activeNetwork.id,
    ...eventPayload,
    page: trackingLocation,
    ...(sharedList ? { sharedList: sharedList.id } : undefined),
  });
};
